import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware'
import { Lang } from "../wallet/product.model";
import i18n from '@/utils/i18n.utils';

const ADAMLIPPES_BRAND_NAME = 'adamlippes';

const BROWSER_LANGUAGE_MAP: Record<string, Lang> = {
  en: 'EN',
  fr: 'FR',
  ja: 'JA',
};

const normalizeBrandName = (brandName: string): string => {
  return brandName.toLowerCase().replace(/\s+/g, '');
}

const canUseJapanese = (brandName: string): boolean => {
  return normalizeBrandName(brandName) === ADAMLIPPES_BRAND_NAME;
}

const sanitizeLanguageForBrand = (language: Lang, brandName: string): Lang => {
  if (language === 'JA' && !canUseJapanese(brandName)) {
    return 'EN';
  }

  return language;
}

export const getDefaultLanguage = (brandName = ''): Lang => {
  if (typeof navigator === 'undefined') {
    return 'EN';
  }

  const browserLanguage = navigator.language?.toLowerCase() ?? '';
  const primaryLanguage = browserLanguage.split('-')[0];
  const defaultLanguage = BROWSER_LANGUAGE_MAP[primaryLanguage] ?? 'EN';
  const nextLanguage = brandName ? sanitizeLanguageForBrand(defaultLanguage, brandName) : defaultLanguage;
  return nextLanguage;
}

interface SettingsState {
  language: Lang,
  brandName: string,
}

interface SettingsActions {
  setLanguage: (language: Lang) => void;
  setBrandName: (brandName: string) => void;
  getLanguage: () => Lang;
  getBrandName: () => string;
}

export const SettingsStore = create<SettingsState & SettingsActions>()(
  persist(
    (set, get) => (
      {
        language: getDefaultLanguage(),
        brandName: '',
        setLanguage: language => {
          const brandName = get().brandName;
          const nextLanguage = brandName ? sanitizeLanguageForBrand(language, brandName) : language;
          set({ language: nextLanguage });
          i18n.changeLanguage(nextLanguage);
        },
        setBrandName: brandName => {
          const normalizedBrandName = normalizeBrandName(brandName);
          const currentLanguage = get().language;
          const nextLanguage = sanitizeLanguageForBrand(currentLanguage, normalizedBrandName);

          set({
            brandName: normalizedBrandName,
            language: nextLanguage,
          });

          i18n.changeLanguage(nextLanguage);
        },
        getLanguage: () => {
          const { language } = get();
          return language;
        },
        getBrandName: () => {
          const { brandName } = get();
          return brandName;
        }
      }
    ),
    {
      name: "_settings_wallet_storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
)